import React from 'react';
import styled, { css } from 'styled-components';

const ButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.$size === 'small' ? '6px 12px' : props.$size === 'large' ? '12px 24px' : '8px 16px'};
  font-size: ${props => props.$size === 'small' ? '0.75rem' : props.$size === 'large' ? '1rem' : '0.875rem'};
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: ${props => props.$iconOnly ? '0' : '8px'};
  }
`;

const PrimaryButton = styled.button`
  ${ButtonBase}
  background-color: #3b82f6;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #2563eb;
  }
  
  &:active:not(:disabled) {
    background-color: #1d4ed8;
  }
`;

const SecondaryButton = styled.button`
  ${ButtonBase}
  background-color: #f1f5f9;
  color: #1e293b;
  
  &:hover:not(:disabled) {
    background-color: #e2e8f0;
  }
  
  &:active:not(:disabled) {
    background-color: #cbd5e1;
  }
`;

const OutlineButton = styled.button`
  ${ButtonBase}
  background-color: transparent;
  color: #3b82f6;
  border: 1px solid #3b82f6;
  
  &:hover:not(:disabled) {
    background-color: rgba(59, 130, 246, 0.05);
  }
  
  &:active:not(:disabled) {
    background-color: rgba(59, 130, 246, 0.1);
  }
`;

const DangerButton = styled.button`
  ${ButtonBase}
  background-color: #ef4444;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #dc2626;
  }
  
  &:active:not(:disabled) {
    background-color: #b91c1c;
  }
`;

const SuccessButton = styled.button`
  ${ButtonBase}
  background-color: #10b981;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #059669;
  }
  
  &:active:not(:disabled) {
    background-color: #047857;
  }
`;

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'medium', 
  iconOnly = false,
  fullWidth = false,
  loading = false,
  disabled = false,
  onClick,
  type = 'button',
  ...props 
}) => {
  const ButtonComponent = {
    primary: PrimaryButton,
    secondary: SecondaryButton,
    outline: OutlineButton,
    danger: DangerButton,
    success: SuccessButton
  }[variant] || PrimaryButton;
  
  // Filter out our custom props so they don't get passed to the DOM element
  // Only pass standard HTML button attributes to the DOM
  const domProps = { ...props };
  // Remove any custom props that might cause React warnings
  delete domProps.fullWidth;
  delete domProps.loading;
  delete domProps.iconOnly;
  delete domProps.variant;
  delete domProps.size;
  
  return (
    <ButtonComponent 
      $size={size} 
      $iconOnly={iconOnly}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      data-loading={loading ? 'true' : 'false'}
      aria-busy={loading}
      {...domProps}
    >
      {loading ? 'Loading...' : children}
    </ButtonComponent>
  );
};

export default Button;
